import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";
import { botMessage, useBotCheck } from "@/components/site/BotCheck";

const TOPICS = [["general", "General question"], ["problem", "Report a problem"], ["press", "Press"]];
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// copy: optional Content labels (site_support): topic_general|problem|press, name_label, email_label, message_placeholder, message_counter, send_button.
export default function ContactForm({ max, topic: topicProp, formRef, copy = {} }) {
  const [f, setF] = useState({ topic: "general", name: "", email: "", message: "" });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const bot = useBotCheck("contact");
  useEffect(() => { if (topicProp) setF((s) => ({ ...s, topic: topicProp.value })); }, [topicProp]);
  const set = (k) => (e) => { setF((s) => ({ ...s, [k]: e.target.value })); setErrors((x) => ({ ...x, [k]: null })); setStatus(null); };

  const submit = async (e) => {
    e.preventDefault();
    const err = {};
    if (!EMAIL_RE.test(f.email.trim())) err.email = "Enter a valid email address.";
    if (!f.message.trim()) err.message = "Write a short message.";
    else if (max && f.message.length > max) err.message = `Keep it under ${max} characters.`;
    setErrors(err);
    if (Object.keys(err).length) return;
    setBusy(true);
    try {
      const token = await bot.getToken();
      await api("/public/contact", { auth: false, body: { topic: f.topic, name: f.name.trim() || null, email: f.email.trim(), message: f.message.trim(), ...bot.fields(token) } });
      setStatus({ ok: true, text: "Thanks. Your message is with us, and we'll reply by email." });
      setF((s) => ({ ...s, message: "" }));
    } catch (e2) {
      setStatus({ ok: false, text: botMessage(e2) || (e2.code === "too_long" ? `Keep it under ${e2.data?.max || max} characters.` : e2.message) });
    } finally { bot.reset(); setBusy(false); }
  };
  const fieldErr = (k) => errors[k] && <p id={`cf-${k}-err`} className="cw-error mt-1 text-sm" data-testid={`contact-${k}-error`}>{errors[k]}</p>;

  return (
    <form ref={formRef} onSubmit={submit} noValidate className="relative space-y-5" aria-label="Contact us" data-testid="contact-form">
      {bot.honeypot}
      <fieldset><legend className="mb-2 text-sm font-medium">Topic</legend>
        <div className="flex flex-wrap gap-2" role="radiogroup">
          {TOPICS.map(([k, fallback]) => { const l = copy[`topic_${k}`] || fallback; return (
            <label key={k} className={`s-chip-choice cursor-pointer rounded-full px-4 py-2 text-sm transition-colors focus-within:ring-2 focus-within:ring-ring ${f.topic === k ? "is-on font-semibold" : ""}`}>
              <input type="radio" name="topic" value={k} checked={f.topic === k} onChange={set("topic")} className="sr-only" data-testid={`contact-topic-${k}`} />{l}
            </label>
          ); })}
        </div></fieldset>
      <div className="grid gap-5 sm:grid-cols-2">
        <div><Label htmlFor="cf-name">{copy.name_label || "Name (optional)"}</Label><Input id="cf-name" value={f.name} onChange={set("name")} autoComplete="name" className="mt-2 h-12" data-testid="contact-name-input" /></div>
        <div><Label htmlFor="cf-email">{copy.email_label || "Email"}</Label><Input id="cf-email" type="email" value={f.email} onChange={set("email")} autoComplete="email" aria-invalid={!!errors.email} aria-describedby={errors.email ? "cf-email-err" : undefined} className="mt-2 h-12" data-testid="contact-email-input" />{fieldErr("email")}</div>
      </div>
      <div><Label htmlFor="cf-msg">Message</Label>
        <Textarea id="cf-msg" rows={6} maxLength={max || undefined} value={f.message} onChange={set("message")} placeholder={copy.message_placeholder} aria-invalid={!!errors.message} aria-describedby="cf-msg-count" className="mt-2" data-testid="contact-message-input" />
        <p id="cf-msg-count" className="cw-muted mt-1 text-xs tabular-nums">{copy.message_counter ? copy.message_counter.replace("{n}", f.message.length) : `${f.message.length}${max ? ` / ${max}` : ""}`}</p>{fieldErr("message")}</div>
      {bot.slot}
      {status && <p role={status.ok ? "status" : "alert"} className={`rounded-2xl px-4 py-3 text-sm ${status.ok ? "cw-tone-success" : "cw-tone-error"}`} data-testid={status.ok ? "contact-success" : "contact-error"}>{status.text}</p>}
      <Button type="submit" disabled={busy} className="s-btn-primary h-12 rounded-full px-8" data-testid="contact-submit-button">{busy ? "Sending…" : copy.send_button || "Send message"}</Button>
    </form>
  );
}
